import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Pencil, Power, PowerOff, Undo2 } from "lucide-react";
import type { Paginated } from "@gatiq/shared";
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FilterSelect,
  Modal,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TD,
  TH,
  THead,
  RowActions,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  Toolbar,
  ToolbarCount,
  type Tone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useCan } from "@/auth/access";

/**
 * Every code in one print run, one row each. Unscanned codes can be re-priced
 * or switched off; a scanned code's points are already in someone's balance,
 * so the only thing left to do with it is reverse it.
 */
type CodeState = "active" | "used" | "inactive" | "reversed";

interface CodeRow {
  id: string;
  code: string;
  points: number;
  state: CodeState;
  usedBy: { id: string; name: string | null } | null;
  usedAt: string | null;
}

import { ListFilters, useListFilters } from "@/components/ListFilters";
const PAGE_SIZE = 50;

const STATES: Record<CodeState, { label: string; tone: Tone }> = {
  active: { label: "Active", tone: "success" },
  used: { label: "Scanned", tone: "info" },
  inactive: { label: "Inactive", tone: "neutral" },
  reversed: { label: "Reversed", tone: "danger" },
};

export function RewardCodesPage() {
  const { batchId, orgSlug } = useParams();
  const toast = useToast();
  const canEdit = Boolean(useCan()("rewards", "edit"));
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [data, setData] = useState<Paginated<CodeRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [batchLabel, setBatchLabel] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<CodeRow | null>(null);
  const [points, setPoints] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const [reverseTarget, setReverseTarget] = useState<CodeRow | null>(null);
  const [reason, setReason] = useState("");

  // Selection is per page: it is cleared whenever the list reloads, so a
  // bulk action only ever touches rows the admin can currently see.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ items: { id: string; label: string }[] }>("/org/rewards/batches")
      .then((res) => setBatchLabel(res.items.find((b) => b.id === batchId)?.label ?? ""))
      .catch(() => undefined);
  }, [batchId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(query);
      if (q.trim()) query.set("q", q.trim());
      if (state) query.set("state", state);
      setData(await api.get<Paginated<CodeRow>>(`/org/rewards/batches/${batchId}/codes?${query}`));
      setSelected(new Set());
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load codes.");
    } finally {
      setLoading(false);
    }
  }, [batchId, page, q, state, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(row: CodeRow): Promise<void> {
    setBusyId(row.id);
    try {
      await api.patch(`/org/rewards/codes/${row.id}`, { active: row.state !== "active" });
      toast.success(`${row.code} ${row.state === "active" ? "deactivated" : "activated"}.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not change this code.");
    } finally {
      setBusyId(null);
    }
  }

  async function bulkSetActive(active: boolean): Promise<void> {
    setBulkBusy(true);
    try {
      const res = await api.patch<{ changed: number }>(`/org/rewards/batches/${batchId}/codes`, {
        ids: [...selected],
        active,
      });
      const skipped = selected.size - res.changed;
      toast.success(
        `${res.changed} ${res.changed === 1 ? "code" : "codes"} ${active ? "activated" : "deactivated"}` +
          (skipped > 0 ? ` (${skipped} already ${active ? "active" : "inactive"}).` : "."),
      );
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not change these codes.");
    } finally {
      setBulkBusy(false);
    }
  }

  // Scanned and reversed codes cannot be switched, so they are not selectable.
  const selectable = canEdit ? (data?.items ?? []).filter((c) => c.state === "active" || c.state === "inactive") : [];
  const allSelected = selectable.length > 0 && selectable.every((c) => selected.has(c.id));

  function toggleRow(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function savePoints(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!editTarget) return;
    setEditError(null);
    setBusyId(editTarget.id);
    try {
      await api.patch(`/org/rewards/codes/${editTarget.id}`, { points: Number(points) });
      toast.success(`${editTarget.code} is now worth ${points} QCoin.`);
      setEditTarget(null);
      await load();
    } catch (err) {
      setEditError(err instanceof ApiClientError ? err.message : "Could not change the points.");
    } finally {
      setBusyId(null);
    }
  }

  async function reverse(): Promise<void> {
    if (!reverseTarget) return;
    setBusyId(reverseTarget.id);
    try {
      const res = await api.post<{ reversed: number }>(`/org/rewards/codes/${reverseTarget.id}/reverse`, {
        reason: reason.trim() || undefined,
      });
      toast.success(`${reverseTarget.code} reversed. ${res.reversed} QCoin taken back.`);
      setReverseTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not reverse this code.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Page bleed>
      <PageHeader
        action={
          <Link to={`/${orgSlug}/admin/rewards`}>
            <Button icon={<ArrowLeft aria-hidden="true" className="size-4" />} variant="secondary">
              All batches
            </Button>
          </Link>
        }
        title={batchLabel ? `Codes in "${batchLabel}"` : "Codes"}
      />

      <Card fill>
        <Toolbar trailing={data ? <ToolbarCount count={data.total} noun="code" /> : null}>
          <SearchInput
            label="Search by code"
            onChange={(v) => {
              setQ(v);
              setPage(1);
            }}
            placeholder="Search code…"
            value={q}
          />
          <FilterSelect
            allLabel="All"
            label="Status"
            onChange={(v) => {
              setState(v);
              setPage(1);
            }}
            options={Object.entries(STATES).map(([value, s]) => ({ value, label: s.label }))}
            value={state}
          />
          <ListFilters definitions={data?.filters} filters={filters} />
        </Toolbar>

        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-rule bg-brand-light/50 px-4 py-2 text-sm">
            <span className="font-medium text-ink">{selected.size} selected</span>
            <Button loading={bulkBusy} onClick={() => void bulkSetActive(false)} size="sm" variant="secondary">
              Deactivate selected
            </Button>
            <Button loading={bulkBusy} onClick={() => void bulkSetActive(true)} size="sm" variant="secondary">
              Activate selected
            </Button>
            <Button onClick={() => setSelected(new Set())} size="sm" variant="ghost">
              Clear
            </Button>
          </div>
        ) : null}

        {loading && !data ? (
          <TableSkeleton cols={6} />
        ) : data && data.items.length > 0 ? (
          <>
            <Table fill searchable={false} filterable={false}>
              <THead>
                <TR>
                  <TH>
                    <input
                      aria-label="Select all codes on this page"
                      checked={allSelected}
                      disabled={selectable.length === 0}
                      onChange={() =>
                        setSelected(allSelected ? new Set() : new Set(selectable.map((c) => c.id)))
                      }
                      type="checkbox"
                    />
                  </TH>
                  <TH>Code</TH>
                  <TH align="right">Points</TH>
                  <TH>Status</TH>
                  <TH>Scanned by</TH>
                  <TH align="right" width="4.5rem">
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <tbody>
                {data.items.map((c) => {
                  const editable = c.state === "active" || c.state === "inactive";
                  return (
                    <TR key={c.id}>
                      <TD>
                        <input
                          aria-label={`Select ${c.code}`}
                          checked={selected.has(c.id)}
                          disabled={!editable || !canEdit}
                          onChange={() => toggleRow(c.id)}
                          type="checkbox"
                        />
                      </TD>
                      <TD>
                        <span className="font-mono text-ink">{c.code}</span>
                      </TD>
                      <TD align="right">{c.points.toLocaleString("en-IN")}</TD>
                      <TD>
                        <Badge tone={STATES[c.state].tone}>{STATES[c.state].label}</Badge>
                      </TD>
                      <TD>
                        {c.usedBy ? (
                          <>
                            <span className="text-ink">{c.usedBy.name ?? "Unknown"}</span>
                            {c.usedAt ? (
                              <span className="block text-xs text-muted">
                                {new Date(c.usedAt).toLocaleString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </TD>
                      <TD align="right">
                        <RowActions
                          busy={busyId === c.id}
                          items={[
                            {
                              label: "Edit points",
                              icon: Pencil,
                              hidden: !editable || !canEdit,
                              onSelect: () => {
                                setPoints(String(c.points));
                                setEditError(null);
                                setEditTarget(c);
                              },
                            },
                            {
                              label: c.state === "active" ? "Deactivate" : "Activate",
                              icon: c.state === "active" ? PowerOff : Power,
                              hidden: !editable || !canEdit,
                              onSelect: () => void toggle(c),
                            },
                            {
                              label: "Reverse scan",
                              icon: Undo2,
                              danger: true,
                              hidden: c.state !== "used" || !canEdit,
                              onSelect: () => {
                                setReason("");
                                setReverseTarget(c);
                              },
                            },
                          ]}
                          label={c.code}
                        />
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
            <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
          </>
        ) : (
          <EmptyState message="Try a different code or status." title="No codes match" />
        )}
      </Card>

      <Modal onClose={() => setEditTarget(null)} open={editTarget !== null} title={`Edit ${editTarget?.code ?? ""}`}>
        <form className="flex flex-col gap-4" noValidate onSubmit={(e) => void savePoints(e)}>
          {editError ? <Alert kind="danger">{editError}</Alert> : null}
          <TextInput
            autoFocus
            inputMode="numeric"
            label="Points for this code"
            onChange={(e) => setPoints(e.target.value.replace(/\D/g, ""))}
            required
            value={points}
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditTarget(null)} type="button" variant="secondary">
              Cancel
            </Button>
            <Button disabled={!(Number(points) > 0)} loading={busyId === editTarget?.id} type="submit">
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        confirmLabel="Reverse points"
        loading={busyId === reverseTarget?.id}
        message={
          reverseTarget ? (
            <div className="flex flex-col gap-3">
              <p>
                Takes {reverseTarget.points.toLocaleString("en-IN")} QCoin back from{" "}
                {reverseTarget.usedBy?.name ?? "the karigar"}, along with any counter or distributor
                commission, and switches this code off for good. It shows in their history as a
                reversal.
              </p>
              <TextInput
                label="Reason (optional)"
                maxLength={200}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Scanned from unsold stock"
                value={reason}
              />
            </div>
          ) : null
        }
        onClose={() => setReverseTarget(null)}
        onConfirm={() => void reverse()}
        open={reverseTarget !== null}
        title={reverseTarget ? `Reverse ${reverseTarget.code}?` : ""}
      />
    </Page>
  );
}
